import { useState, useMemo, useEffect, useRef } from 'react'
import { SearchIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
// @ts-ignore
import { EntityModelJson } from '../lib/api'

// ── Color map ─────────────────────────────────────────────────────────────────

interface TypeColors {
  bg: string
  border: string
  text: string
  accent: string
  countBg: string
}

const TYPE_COLORS: Record<string, TypeColors> = {
  'Company (Root)': { bg: '#f5f3ff', border: '#c4b5fd', text: '#4c1d95', accent: '#7c3aed', countBg: '#ede9fe' },
  'Audience Persona': { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8', accent: '#2563eb', countBg: '#dbeafe' },
  'Industry Vertical': { bg: '#ecfeff', border: '#67e8f9', text: '#0e7490', accent: '#0891b2', countBg: '#cffafe' },
  'Product Capability': { bg: '#f0fdf4', border: '#86efac', text: '#15803d', accent: '#16a34a', countBg: '#dcfce7' },
  'Use Case': { bg: '#ecfdf5', border: '#6ee7b7', text: '#047857', accent: '#059669', countBg: '#d1fae5' },
  'Competitor': { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', accent: '#dc2626', countBg: '#fee2e2' },
  'Objection / Misconception': { bg: '#fff7ed', border: '#fdba74', text: '#c2410c', accent: '#ea580c', countBg: '#ffedd5' },
  'Objection / Concern': { bg: '#fffbeb', border: '#fcd34d', text: '#b45309', accent: '#d97706', countBg: '#fef9c3' },
  'Experience Enablement Service': { bg: '#eef2ff', border: '#a5b4fc', text: '#3730a3', accent: '#4f46e5', countBg: '#e0e7ff' },
  'Strategic Partner': { bg: '#f0fdfa', border: '#5eead4', text: '#0f766e', accent: '#0d9488', countBg: '#ccfbf1' },
  'Case Study': { bg: '#f0f9ff', border: '#7dd3fc', text: '#0369a1', accent: '#0284c7', countBg: '#e0f2fe' },
}

const DEFAULT_COLORS: TypeColors = {
  bg: '#f9fafb', border: '#d1d5db', text: '#374151', accent: '#6b7280', countBg: '#f3f4f6',
}

function getColors(type: string): TypeColors {
  return TYPE_COLORS[type] ?? DEFAULT_COLORS
}

// ── Animated side panel ───────────────────────────────────────────────────────

interface SidePanelProps {
  entity: Record<string, any> | null
  isOpen: boolean
  onClose: () => void
}

function SidePanel({ entity, isOpen, onClose }: SidePanelProps) {
  const [activeSection, setActiveSection] = useState<'attrs' | 'rels' | null>(null)

  // Reset to collapsed when a new entity loads
  useEffect(() => {
    setActiveSection(null)
  }, [entity?.branch_id])

  const toggleSection = (section: 'attrs' | 'rels') =>
    setActiveSection((prev) => (prev === section ? null : section))

  const c = entity ? getColors(entity.entity_type) : DEFAULT_COLORS
  const attrEntries = Object.entries(entity?.key_attributes ?? {}).filter(([, v]) => v !== '' && v != null)
  const relEntries = Object.entries(entity?.relationships ?? {}).filter(([, v]) => v !== '' && v != null)

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 z-50 shadow-2xl flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {entity && (
          <>
            {/* Header */}
            <div
              className="px-5 pt-5 pb-4 border-b flex-shrink-0"
              style={{ backgroundColor: c.bg, borderColor: c.border }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: c.accent }}>
                    {entity.entity_type}
                    {entity.branch_id && entity.branch_id !== 'ROOT' && (
                      <span className="font-mono normal-case font-normal ml-2 opacity-60">{entity.branch_id}</span>
                    )}
                  </p>
                  <p className="text-base font-bold text-gray-900 leading-snug">{entity.name}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-xl hover:bg-black/10 transition-colors flex-shrink-0"
                >
                  <XIcon className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 flex flex-col">

              {/* Description */}
              {entity.description && (
                <div className="px-5 py-4 border-b border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{entity.description}</p>
                </div>
              )}

              {/* Key Attributes */}
              {attrEntries.length > 0 && (
                <div className="border-b border-gray-100">
                  <button
                    onClick={() => toggleSection('attrs')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xs font-semibold text-gray-700">Key Attributes ({attrEntries.length})</span>
                    {activeSection === 'attrs'
                      ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400" />
                      : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                    }
                  </button>
                  {activeSection === 'attrs' && (
                    <div className="px-5 pb-4 divide-y divide-gray-100">
                      {attrEntries.map(([k, v]) => (
                        <div key={k} className="py-3 first:pt-0 last:pb-0">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{k}</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{String(v)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Relationships */}
              {relEntries.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleSection('rels')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xs font-semibold text-gray-700">Relationships ({relEntries.length})</span>
                    {activeSection === 'rels'
                      ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400" />
                      : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                    }
                  </button>
                  {activeSection === 'rels' && (
                    <div className="px-5 pb-4 divide-y divide-gray-100">
                      {relEntries.map(([k, v]) => (
                        <div key={k} className="py-3 first:pt-0 last:pb-0">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{k}</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{String(v)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>{/* end scrollable body */}
          </>
        )}
      </div>
    </>
  )
}

// ── Entity card ───────────────────────────────────────────────────────────────

function EntityCard({ entity, onSelect }: { entity: Record<string, any>; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className="w-full text-left bg-white border border-gray-200 rounded-xl p-3.5
        hover:border-gray-400 hover:shadow-md
        active:scale-[0.98] active:shadow-sm
        transition-all duration-150 group"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-gray-700 flex-1 min-w-0">
          {entity.name}
        </p>
        {entity.branch_id && entity.branch_id !== 'ROOT' && (
          <span className="text-[9px] font-mono text-gray-400 flex-shrink-0 mt-0.5 bg-gray-50 px-1.5 py-0.5 rounded">
            {entity.branch_id}
          </span>
        )}
      </div>
      {entity.description && (
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{entity.description}</p>
      )}
    </button>
  )
}

// ── Entity type column ────────────────────────────────────────────────────────

function EntityColumn({
  entityType, entities, searchQuery, onSelect,
}: {
  entityType: string
  entities: Record<string, any>[]
  searchQuery: string
  onSelect: (e: Record<string, any>) => void
}) {
  const c = getColors(entityType)

  const filtered = useMemo(() => {
    if (!searchQuery) return entities
    const q = searchQuery.toLowerCase()
    return entities.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q),
    )
  }, [entities, searchQuery])

  if (filtered.length === 0 && searchQuery) return null

  return (
    <div className="flex-shrink-0 w-72 flex flex-col rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
      {/* Column header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-shrink-0"
        style={{ backgroundColor: c.bg, borderColor: c.border }}
      >
        <p className="text-xs font-bold leading-tight" style={{ color: c.text }}>
          {entityType}
        </p>
        <span
          className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
          style={{ backgroundColor: c.countBg, color: c.text }}
        >
          {filtered.length}
          {searchQuery && filtered.length !== entities.length && (
            <span className="opacity-60">/{entities.length}</span>
          )}
        </span>
      </div>

      {/* Entity cards */}
      <div className="overflow-y-auto flex-1 p-2.5 space-y-2" style={{ maxHeight: 640 }}>
        {filtered.map((entity, i) => (
          <EntityCard key={entity.branch_id ?? i} entity={entity} onSelect={() => onSelect(entity)} />
        ))}
        {filtered.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">No results</p>
        )}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

interface EntityModelColumnsProps {
  model: EntityModelJson
}

export default function EntityModelColumns({ model }: EntityModelColumnsProps) {
  const [search, setSearch] = useState('')
  const [selectedEntity, setSelectedEntity] = useState<Record<string, any> | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Open panel with slide animation
  const openPanel = (entity: Record<string, any>) => {
    setSelectedEntity(entity)
    // Let the DOM render with translate-x-full first, then slide in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setPanelOpen(true))
    })
  }

  // Close with slide-out animation, then clear entity
  const closePanel = () => {
    setPanelOpen(false)
    setTimeout(() => setSelectedEntity(null), 300)
  }

  // Group entities by type (exclude root)
  const groups = useMemo(() => {
    const map: Record<string, Record<string, any>[]> = {}
    for (const e of model.entities ?? []) {
      const t = e.entity_type ?? 'Other'
      if (!map[t]) map[t] = []
      map[t].push(e)
    }
    return map
  }, [model])

  const typeCount = Object.keys(groups).length
  const totalEntities = model.total_entities ?? (model.entities?.length ?? 0)

  const matchCount = useMemo(() => {
    if (!search) return totalEntities
    const q = search.toLowerCase()
    return Object.values(groups).flat().filter(
      (e) => e.name?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q),
    ).length
  }, [search, groups, totalEntities])

  return (
    <div>
      <div className="mt-5 space-y-3">
        {/* ── Stats header ── */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            <div className="px-6 py-5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Company</p>
              <p className="text-xl font-bold text-gray-900">{model.company}</p>
            </div>
            <div className="px-6 py-5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Total Entities</p>
              <p className="text-3xl font-bold text-gray-900">
                {search ? (
                  <>
                    <span className="text-primary-600">{matchCount}</span>
                    <span className="text-lg text-gray-300 font-normal"> / {totalEntities}</span>
                  </>
                ) : totalEntities}
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Entity Types</p>
              <p className="text-3xl font-bold text-gray-900">{typeCount}</p>
            </div>
          </div>
        </div>

        {/* ── Search card ── */}
        <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transition-colors" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entities by name or description…"
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg
              focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:bg-white
              transition-all duration-200 placeholder-gray-400"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); searchRef.current?.focus() }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
              >
                <XIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          {search && (
            <p className="text-xs text-gray-400 mt-2 pl-1">
              Showing <span className="font-semibold text-gray-700">{matchCount}</span> of {totalEntities} entities
            </p>
          )}
        </div>

        {/* ── Columns board ── */}
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-max">
            {Object.entries(groups).map(([type, entities]) => (
              <EntityColumn
                key={type}
                entityType={type}
                entities={entities}
                searchQuery={search}
                onSelect={openPanel}
              />
            ))}
          </div>
        </div>
      </div>
      {/* ── Animated side panel (always rendered, slide in/out) ── */}
      <SidePanel entity={selectedEntity} isOpen={panelOpen} onClose={closePanel} />
    </div>
  )
}
